import {makeAutoObservable} from "mobx";

import {MobxManager} from "framework/mobx-integration";
import {WidgetProps} from "controls/designer/features/widgets/widgetProps";

import {i18n} from "core/localization";
import {ExternalStateProvider} from "controls/grid/stateProviders";
import {deserialize} from "serializr";
import {GridState} from "controls/grid/gridState";
import {StylesFromMxGraphCellPlugin} from "controls/grid/plugins/stylesFromMxGraphCellPlugin";
import {DataSourceDataGridStore} from "areas/data-sources/stores/dataSourceDataGridStore";
import {newGuid} from "tools/guid";
import {apiFetch} from "framework/api";
import {clearDatasourceCache} from "controls/designer/features/widgets/datasources/api";
import {TimePeriod} from "controls/react/form/timePeriodSelector";
import {DataSourceDataGridWidgetPersistedState, DataSourceDataGridWidgetConfig} from "./types";

export class DataSourceDataGridWidgetStore {
	dataSourceDataGridStore: DataSourceDataGridStore
	errorMessage: string
	timePeriod: TimePeriod
	mobx = new MobxManager();
	requestId = newGuid();


	constructor(public props: WidgetProps<DataSourceDataGridWidgetConfig, DataSourceDataGridWidgetPersistedState>) {
		this.timePeriod = this.props.config.period
		makeAutoObservable(this)
	}

	get showTimePeriodSelector() {
		return !!this.props.config.period && this.props.config.hideTimePeriodSelector !== true
	}

	async init() {
		this.dataSourceDataGridStore = new DataSourceDataGridStore()
		this.dataSourceDataGridStore.props = {...this.props.config, requestId: this.requestId}

		const gridConfig = await this.dataSourceDataGridStore.getDefaultGridConfig()

		if(gridConfig == null){
			this.errorMessage = i18n('Data not found');
			return
		}

		gridConfig.stateProviders ??= []
		gridConfig.stateProviders.push(
			new ExternalStateProvider(deserialize<GridState<any>>(GridState, this.props.persistedState?.grid)),
		)
		gridConfig.plugins ??= []
		gridConfig.plugins.push(new StylesFromMxGraphCellPlugin(this.props.designer.store, this.props.cell))

		this.dataSourceDataGridStore.initStore(gridConfig)
		this.mobx.reaction(
			() => [this.dataSourceDataGridStore.gridStore?.state?.customPayload, this.timePeriod] as const,
			([payload, timePeriod]) => {
				if (!payload) return

				payload.timePeriod = timePeriod
				payload.timePeriodColumn = this.props.config.timePeriodColumn
			},
			{fireImmediately: true},
		)
	}

	onPeriodChange = (period: TimePeriod) => {
		this.timePeriod = period
	}

	destroy() {
		this.dataSourceDataGridStore?.destroy()
		this.mobx.destroy();
		void apiFetch(clearDatasourceCache({accountId: this.props.config.accountId, requestId: this.requestId}));
	}
}
